import React, { useCallback, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import { KeywordPicker } from 'common';
import { useFeatureFlags } from 'providers/FeatureFlagsProvider';
import strings from 'strings';
import TextBox from 'UI/components/atoms/TextBox';
import { When } from 'UI/components/atoms/When';
import AutocompleteSelect from 'UI/components/molecules/AutocompleteSelect';
import { FORM_SPACING, gridDimensions, minWidthTextBox } from 'UI/constants/dimensions';
import { Endpoints } from 'UI/constants/endpoints';
import { FeatureFlags } from 'UI/constants/featureFlags';
import {
  EXT_PHONE_VALIDATION,
  idOptionSelected,
  industrySpecialtyOptionLabel,
  NAME_VALIDATION,
  NICKNAME_VALIDATION,
  PHONE_VALIDATION,
  TITLE_VALIDATION,
  titleOptionLabel,
  URL_VALIDATION,
  VALIDATION_REGEXS
} from 'UI/utils';
import { DEFAULT_DISPLAY_TEXT_KEY } from 'UI/utils/inventory';

import PhoneNumbersForm from '../PhoneNumbersForm';
import { DEFAULT_PHONE_ROW } from '../PhoneNumbersForm/utils';

import {
  CHAINED_SELECTS,
  FIELD_KEYS,
  HA_STATUS_KEY,
  PHONE_PROPS,
  SUB_STATUS_KEY
} from './HiringAuthorityForm.contants';
import { useStatus } from './HiringAuthorityForm.hooks';
import { useStyles } from './styles';

const { xs, sm, md, lg } = gridDimensions.editionDrawer;

/**
 * @param {Object} props
 * @param {Map} props.initialValues
 * @param {boolean} props.isStatusSelectionEnabled
 */
const HiringAuthorityForm = ({ initialValues, isStatusSelectionEnabled }) => {
  const classes = useStyles();
  const [comboValues, setComboValues] = useState(initialValues);
  const [hasSubspecialties, setHasSubspecialties] = useState(false);

  const { checkIfFeatureFlagEnabled } = useFeatureFlags();

  const isMultiplePhonesEnabled = checkIfFeatureFlagEnabled(
    FeatureFlags.HiringAuthoritiesMultiplePhones
  );
  const shouldDisplayLegacyPhoneFields = !isMultiplePhonesEnabled;

  const { register, unregister, errors, setValue, getValues, watch } = useFormContext();

  const formValues = watch();
  const isExistingNameSelected = !!formValues.existingHiringAuthority?.id;
  const isCreatingNewHiringAuth = !isExistingNameSelected;
  const selectedStatus = comboValues?.[HA_STATUS_KEY];
  const selectedSubStatus = comboValues?.[SUB_STATUS_KEY];
  const {
    availableStatusOptions,
    handleStatusOptionsLoaded,
    isSubStatusEnabled,
    selectedStatusValue,
    statusEndpoint,
    subStatusEndpoint
  } = useStatus({ selectedStatus });

  useEffect(() => {
    setValue('specialty', comboValues.specialty_id);
    setValue('subspecialty', comboValues.subspecialty_id);
    setValue('position', comboValues.position_id);
  }, [comboValues, setValue]);

  useEffect(() => {
    if (hasSubspecialties) {
      register({ name: 'subspecialty_id' }, { required: 'Subspecialty is required' });
    } else {
      register({ name: 'subspecialty_id' });
      setValue('subspecialty_id', null, { shouldValidate: true, shouldDirty: true });
    }
  }, [register, hasSubspecialties, setValue]);

  useEffect(() => {
    register({ name: 'specialty' });
    register({ name: 'subspecialty' });
    register({ name: 'position' });
    register({ name: 'specialty_id' }, { required: 'Industry: Specialty is required' });
    register(
      { name: 'position_id' },
      { required: `${strings.shared.fields.jobFunction} is required` }
    );
    register({ name: 'keywordTags' });

    !isMultiplePhonesEnabled && register({ name: 'personal_phone' }, { ...PHONE_VALIDATION });
    isMultiplePhonesEnabled &&
      register({
        name: 'phones',
        value: isCreatingNewHiringAuth ? [DEFAULT_PHONE_ROW] : []
      });

    !isMultiplePhonesEnabled &&
      register(
        { name: 'work_phone' },
        {
          required: 'Phone is required',
          ...PHONE_VALIDATION
        }
      );

    return () => {
      FIELD_KEYS.forEach(field => unregister(field));
    };
  }, [register, unregister, isCreatingNewHiringAuth, isMultiplePhonesEnabled]);

  useEffect(() => {
    if (isStatusSelectionEnabled) {
      register(
        { name: HA_STATUS_KEY },
        {
          required: strings.formatString(strings.shared.fields.fieldNameRequired, {
            name: strings.shared.fields.status
          })
        }
      );
      if (isSubStatusEnabled) {
        register({ name: SUB_STATUS_KEY });
      } else {
        unregister(SUB_STATUS_KEY);
      }
    }
  }, [isStatusSelectionEnabled, isSubStatusEnabled, register, unregister]);

  const handleComboChange = (name, value) => {
    setComboValues(prevState => ({ ...prevState, [name]: value }));
    setValue(name, value ? value.id : value, true);

    if (name && CHAINED_SELECTS[name]) {
      CHAINED_SELECTS[name].forEach(chainedSelect => {
        setComboValues(prevState => ({ ...prevState, [chainedSelect]: null }));
        setValue(chainedSelect, null);
      });
    }
  };

  const handleSubspecialtiesLoaded = useCallback(options => {
    setHasSubspecialties(options && options.length);
  }, []);

  const handlePhoneChange = (name, value) => {
    setValue(name, value, true);
    setComboValues({ ...comboValues });
  };

  const handleChangeKeywords = (name, value) => {
    setValue(name, value, true);
  };

  return (
    <Grid container>
      <Grid container item spacing={FORM_SPACING}>
        <Grid item xs={xs} sm={sm} md={md} lg={lg}>
          <TextBox
            name="first_name"
            label="First Name *"
            inputRef={register({ required: 'First Name is required', ...NAME_VALIDATION })}
            error={!!errors.first_name}
            errorText={errors.first_name && errors.first_name.message}
            minWidth={minWidthTextBox}
            noMargin
            inputProps={{
              'aria-label': 'first name'
            }}
          />
        </Grid>
        <Grid item xs={xs} sm={sm} md={md} lg={lg}>
          <TextBox
            name="last_name"
            label="Last Name *"
            noMargin
            inputRef={register({ required: 'Last Name is required', ...NAME_VALIDATION })}
            error={!!errors.last_name}
            errorText={errors.last_name && errors.last_name.message}
            minWidth={minWidthTextBox}
          />
        </Grid>
        <Grid item xs={xs} sm={sm} md={md} lg={lg}>
          <TextBox
            name="nickname"
            label="Nickname"
            inputRef={register(NICKNAME_VALIDATION)}
            error={!!errors.nickname}
            noMargin
            errorText={errors.nickname && errors.nickname.message}
            minWidth={minWidthTextBox}
          />
        </Grid>
        <Grid item xs={xs} sm={sm} md={md} lg={lg}>
          <AutocompleteSelect
            name="specialty_id"
            noMargin
            selectedValue={comboValues.specialty_id}
            placeholder="Industry: Specialty *"
            error={!!errors.specialty_id}
            errorText={errors.specialty_id && errors.specialty_id.message}
            url={Endpoints.Specialties}
            groupBy={option => option.industry_title}
            getOptionLabel={industrySpecialtyOptionLabel}
            renderOption={titleOptionLabel}
            getOptionSelected={idOptionSelected}
            onSelect={handleComboChange}
          />
        </Grid>
        <Grid item xs={xs} sm={sm} md={md} lg={lg}>
          <AutocompleteSelect
            name="subspecialty_id"
            selectedValue={comboValues.subspecialty_id}
            placeholder={`Subspecialty ${hasSubspecialties ? '*' : ''}`}
            error={!!errors.subspecialty_id}
            errorText={errors.subspecialty_id && errors.subspecialty_id.message}
            url={
              comboValues.specialty_id
                ? `${Endpoints.Specialties}/${comboValues.specialty_id.id}/subspecialties`
                : ''
            }
            onSelect={handleComboChange}
            noMargin
            onOptionsLoaded={handleSubspecialtiesLoaded}
          />
        </Grid>
        <Grid item xs={xs} sm={sm} md={md} lg={lg}>
          <AutocompleteSelect
            name="position_id"
            noMargin
            selectedValue={comboValues.position_id}
            placeholder={`${strings.shared.fields.jobFunction} *`}
            error={!!errors.position_id}
            errorText={errors.position_id && errors.position_id.message}
            url={
              comboValues.specialty_id
                ? `${Endpoints.Positions}?specialtyId=${comboValues.specialty_id.id}`
                : ''
            }
            onSelect={handleComboChange}
          />
        </Grid>
        <Grid item xs={xs} sm={sm} md={md} lg={lg}>
          <TextBox
            name="title"
            label="Title *"
            inputRef={register({ required: 'Title is required', ...TITLE_VALIDATION })}
            error={!!errors.title}
            errorText={errors.title && errors.title.message}
            minWidth={minWidthTextBox}
            noMargin
          />
        </Grid>
        <Grid item xs={xs} sm={sm} md={md} lg={lg}>
          <TextBox
            name="work_email"
            label="Email *"
            inputRef={register({
              required: 'Email is required',
              pattern: {
                value: VALIDATION_REGEXS.EMAIL,
                message: 'Email must be valid'
              }
            })}
            error={!!errors.work_email}
            errorText={errors.work_email && errors.work_email.message}
            minWidth={minWidthTextBox}
            noMargin
          />
        </Grid>
        <Grid item xs={xs} sm={sm} md={md} lg={lg}>
          <TextBox
            name="personal_email"
            label="Other Email"
            inputRef={register({
              pattern: {
                value: VALIDATION_REGEXS.EMAIL,
                message: 'Email must be valid'
              }
            })}
            error={!!errors.personal_email}
            errorText={errors.personal_email && errors.personal_email.message}
            minWidth={minWidthTextBox}
            noMargin
          />
        </Grid>
        <Grid item xs={xs} sm={sm} md={md} lg={lg}>
          <TextBox
            name="link_profile"
            label="LinkedIn URL"
            inputRef={register(URL_VALIDATION)}
            error={!!errors.link_profile}
            errorText={errors.link_profile && errors.link_profile.message}
            minWidth={minWidthTextBox}
            noMargin
          />
        </Grid>

        {shouldDisplayLegacyPhoneFields && (
          <>
            <Grid item xs={xs} sm={sm} md={md} lg={lg}>
              <TextBox
                name="work_phone"
                label="Phone *"
                placeholder="(999)-999-9999"
                error={!!errors.work_phone}
                errorText={errors.work_phone && errors.work_phone.message}
                minWidth={minWidthTextBox}
                onChange={handlePhoneChange}
                value={getValues('work_phone') || ''}
                inputType="phone"
                inputProps={PHONE_PROPS}
                noMargin
              />
            </Grid>
            <Grid item xs={xs} sm={sm} md={md} lg={lg}>
              <TextBox
                name="ext"
                label="Ext"
                placeholder="999"
                inputRef={register({ ...EXT_PHONE_VALIDATION })}
                error={!!errors.ext}
                errorText={errors.ext && errors.ext.message}
                minWidth={minWidthTextBox}
                noMargin
              />
            </Grid>
            <Grid item xs={xs} sm={sm} md={md} lg={lg}>
              <TextBox
                name="personal_phone"
                label="Other Phone"
                placeholder="(999)-999-9999"
                error={!!errors.personal_phone}
                errorText={errors.personal_phone && errors.personal_phone.message}
                minWidth={minWidthTextBox}
                onChange={handlePhoneChange}
                value={getValues('personal_phone') || ''}
                inputType="phone"
                noMargin
              />
            </Grid>
            <Grid item xs={xs} sm={sm} md={md} lg={lg}>
              <TextBox
                name="other_ext"
                label="Ext"
                placeholder="999"
                inputRef={register({ ...EXT_PHONE_VALIDATION })}
                error={!!errors.other_ext}
                errorText={errors.other_ext && errors.other_ext.message}
                minWidth={minWidthTextBox}
                noMargin
              />
            </Grid>
          </>
        )}

        {isStatusSelectionEnabled && (
          <>
            <Grid item xs={xs} sm={sm} md={md} lg={lg}>
              <AutocompleteSelect
                name={HA_STATUS_KEY}
                defaultOptions={availableStatusOptions}
                error={!!errors?.[HA_STATUS_KEY]}
                errorText={errors?.[HA_STATUS_KEY]?.message}
                getOptionSelected={idOptionSelected}
                noMargin
                onOptionsLoaded={handleStatusOptionsLoaded}
                onSelect={handleComboChange}
                placeholder={`${strings.shared.fields.status} *`}
                renderOption={titleOptionLabel}
                selectedValue={selectedStatusValue}
                url={statusEndpoint}
              />
            </Grid>
            <When condition={isSubStatusEnabled}>
              <Grid item xs={xs} sm={sm} md={md} lg={lg}>
                <AutocompleteSelect
                  name={SUB_STATUS_KEY}
                  error={!!errors?.[SUB_STATUS_KEY]}
                  errorText={errors?.[SUB_STATUS_KEY]?.message}
                  getOptionSelected={idOptionSelected}
                  noMargin
                  onSelect={handleComboChange}
                  placeholder={strings.shared.fields.subStatus}
                  renderOption={titleOptionLabel}
                  selectedValue={selectedSubStatus}
                  url={subStatusEndpoint}
                />
              </Grid>
            </When>
          </>
        )}
      </Grid>

      <Grid container spacing={2} className={classes.keywordsContainer}>
        <Grid item xs={12}>
          <Typography variant="subtitle1">
            {strings.inventoryProfiles.common.keywords.title}
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <KeywordPicker
            allowNewItems
            displayTextKey={DEFAULT_DISPLAY_TEXT_KEY}
            endpoint={Endpoints.KeywordTags}
            name="keywordTags"
            onSelect={handleChangeKeywords}
            placeholder={strings.inventoryProfiles.common.keywords.placeholder}
            selectedValue={formValues?.keywordTags ?? []}
            typeaheadLimit={25}
          />
        </Grid>
      </Grid>

      {isMultiplePhonesEnabled && <PhoneNumbersForm isEditing isRequired />}
    </Grid>
  );
};

HiringAuthorityForm.defaultProps = {
  initialValues: {},
  isStatusSelectionEnabled: false
};

export default HiringAuthorityForm;
